import type {
  ChildLinkRequestItem,
  IncomingChildLinkRequestItem,
  LinkedFacilityApiRow,
} from "@services/facilityNetworkLink";
import type { UnlinkRequestItem } from "@services/facilityNetworkUnlink";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import {
  bareNpiFromCcn,
  resolveFacilityRegistryDisplayIds,
} from "@utils/facilityUtils";
import {
  abbreviateFacilityType,
  isRegistryPlaceholderName,
  type LinkedFacilityNpiHit,
} from "./facilityNetworkChildrenUtils";
import { expiryDaysFromCreatedAt, INVITE_TTL_DAYS } from "./parentLinkRequestUtils";
import { formatUnlinkRequestedDate } from "./facilityNetworkUnlinkRequestUtils";

export type InboxPill = "link" | "unlink" | "invite";
export type InboxBucket = "needs" | "awaiting" | "resolved";
export type InboxDirection = "incoming" | "outgoing";
export type InboxOutcome = "approved" | "rejected" | "declined" | "cancelled" | "accepted" | null;
export type InboxVariant =
  | "incoming_unlink"
  | "incoming_link"
  | "outgoing_link"
  | "outgoing_unlink"
  | "invite_needed"
  | "resolved_unlink"
  | "resolved_link";

export type NetworkInboxItem = {
  key: string;
  requestId: number;
  kind: InboxPill;
  direction: InboxDirection;
  bucket: InboxBucket;
  variant: InboxVariant;
  pill: InboxPill;
  facilityName: string;
  metaLabel: string;
  outcomePill: InboxOutcome;
  status: string;
  createdAt?: string | null;
  resolvedAt?: string | null;
  expiresInDays: number | null;
  npi?: string | null;
  ccn?: string | null;
  facilityType?: string | null;
  city?: string | null;
  state?: string | null;
  location?: string | null;
  reason?: string | null;
  resolutionNote?: string | null;
  inviteEmail?: string | null;
  currentlyLabel?: string | null;
  childFacilityId?: number | null;
  parentFacilityId?: number | null;
  initiatorRole?: "parent" | "child";
  actionFacilityId: number;
  /** AB#10941 / #10946 */
  coordinationMode?: boolean;
  currentParentFacilityName?: string | null;
  /** After conflict accept — unlink to B pending; treat as awaiting */
  coordinationUnlinkId?: number | null;
};

const PENDING_LINK = new Set(["link_pending", "pending"]);
const RESOLVED_LINK = new Set(["accepted", "declined", "cancelled"]);
const RESOLVED_UNLINK = new Set(["approved", "rejected", "cancelled"]);

/** Strip bypass `username_` prefix; never label NPI as CCN. */
export function inboxRegistryIds(
  npi?: string | null,
  ccn?: string | null,
): { npi: string; ccn: string } {
  const resolved = resolveFacilityRegistryDisplayIds({
    npi_number: npi ?? undefined,
    ccn_number: ccn ?? undefined,
  });
  return {
    npi: resolved.npi || bareNpiFromCcn(npi) || bareNpiFromCcn(ccn) || "",
    ccn: resolved.ccn || "",
  };
}

export function firstFacilityWord(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export function formatInboxFullDate(iso?: string | null): string {
  return formatUnlinkRequestedDate(iso);
}

export function daysLeftLabel(days: number | null | undefined): string {
  if (days == null) return "—";
  if (days <= 0) return "Expired";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

export function formatExpiresIn(days: number | null | undefined): string {
  if (days == null) return "—";
  if (days <= 0) return "Expired";
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function formatFacilityTypeLine(facilityType?: string | null): string {
  if (!facilityType?.trim()) return "—";
  const fake = {
    facility_type: facilityType,
    facility_type_slug: facilityType.toLowerCase().replace(/\s+/g, "_"),
  } as FacilityHierarchyInput;
  const abbrev = abbreviateFacilityType(fake);
  if (!abbrev || abbrev === "—" || abbrev === facilityType) return facilityType;
  return `${abbrev} · ${facilityType}`;
}

export function formatLocationLine(
  city?: string | null,
  state?: string | null,
  location?: string | null,
): string {
  if (location?.trim()) return location.trim();
  const parts = [city, state].filter((part) => Boolean(part && part.trim()));
  return parts.length ? parts.join(", ") : "—";
}

export function inviteDisplayName(name?: string | null, npi?: string | null): string {
  const trimmed = name?.trim() || "";
  if (
    trimmed &&
    !/^npi\s/i.test(trimmed) &&
    !/^ccn\s/i.test(trimmed) &&
    !/^facility\s*\(/i.test(trimmed)
  ) {
    return trimmed;
  }
  const ids = inboxRegistryIds(npi);
  if (ids.ccn && ids.npi) return `CCN ${ids.ccn} · NPI ${ids.npi}`;
  if (ids.ccn) return `CCN ${ids.ccn}`;
  if (ids.npi) return `NPI ${ids.npi}`;
  return trimmed || "Facility";
}

export function wrapReasonQuote(reason?: string | null): string {
  const text = reason?.trim();
  if (!text) return "—";
  const unquoted = text.replace(/^["“]+/, "").replace(/["”]+$/, "");
  return `“${unquoted}”`;
}

function linkedMeta(
  linked: LinkedFacilityApiRow[],
  childFacilityId?: number | null,
  npi?: string | null,
  requestId?: number,
): LinkedFacilityApiRow | undefined {
  if (requestId != null) {
    const byRequest = linked.find((row) => row.request_id === requestId);
    if (byRequest) return byRequest;
  }
  if (childFacilityId != null) {
    const byId = linked.find((row) => row.facility_id === childFacilityId);
    if (byId) return byId;
  }
  const needle = (npi || "").trim();
  if (!needle) return undefined;
  return linked.find((row) => (row.npi_number || "").trim() === needle);
}

function sentShortLabel(iso?: string | null): string {
  if (!iso) return "Sent —";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Sent —";
  return `Sent ${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

function unlinkIncomingMetaLabel(iso?: string | null): string {
  // AB#10945 AC4 — "Unlink request · sent {date}"
  if (!iso) return "Unlink request · sent —";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unlink request · sent —";
  const sent = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `Unlink request · sent ${sent}`;
}

function sentAndDaysLeftLabel(iso?: string | null): string {
  const days = expiryDaysFromCreatedAt(iso);
  const left = daysLeftLabel(days);
  if (!left || left === "—") return sentShortLabel(iso);
  return `${sentShortLabel(iso)} · ${left}`;
}

function currentlyLabelForUnlink(item: UnlinkRequestItem): string {
  if (item.facility_role === "Parent facility") return "Your parent facility";
  if (item.facility_role === "Child facility") return "Your child facility";
  return item.initiator_role === "parent" ? "Your parent facility" : "Your child facility";
}

function unlinkOutcome(status: UnlinkRequestItem["status"]): InboxOutcome {
  if (status === "approved") return "approved";
  if (status === "rejected") return "rejected";
  if (status === "cancelled") return "cancelled";
  return null;
}

function linkOutcome(status: string): InboxOutcome {
  if (status === "accepted") return "accepted";
  if (status === "declined") return "declined";
  if (status === "cancelled") return "cancelled";
  return null;
}

export function buildNetworkInboxItems(input: {
  sent: ChildLinkRequestItem[];
  received: IncomingChildLinkRequestItem[];
  initiatedUnlink?: UnlinkRequestItem[];
  receivedUnlink?: UnlinkRequestItem[];
  linked: LinkedFacilityApiRow[];
  parentFacilityId?: number | null;
  selectedFacilityId?: number | null;
}): NetworkInboxItem[] {
  const parentId = input.parentFacilityId ?? null;
  const selectedId = input.selectedFacilityId ?? null;
  const initiatedUnlink = input.initiatedUnlink ?? [];
  const receivedUnlink = input.receivedUnlink ?? [];
  const items: NetworkInboxItem[] = [];

  input.received.forEach((row) => {
    if (!selectedId) return;
    const pending = PENDING_LINK.has(row.status);
    const resolved = RESOLVED_LINK.has(row.status);
    if (!pending && !resolved) return;
    const name =
      row.parent_facility_name?.trim() ||
      (row.parent_npi ? `NPI ${row.parent_npi}` : row.parent_ccn ? `CCN ${row.parent_ccn}` : "Facility");
    // target_npi is the receiver's own NPI — never show it as the sender's.
    const ids = inboxRegistryIds(row.parent_npi, row.parent_ccn);
    // AB#10946 AC9 — after accept, coordination_unlink_id set → Awaiting (waiting on B)
    const awaitingUnlink = pending && Boolean(row.coordination_unlink_id);
    items.push({
      key: `link-in-${row.id}`,
      requestId: row.id,
      kind: "link",
      direction: "incoming",
      bucket: pending ? (awaitingUnlink ? "awaiting" : "needs") : "resolved",
      variant: pending ? "incoming_link" : "resolved_link",
      pill: "link",
      facilityName: name,
      metaLabel: pending ? sentAndDaysLeftLabel(row.created_at) : "",
      outcomePill: pending ? null : linkOutcome(row.status),
      status: row.status,
      createdAt: row.created_at,
      resolvedAt: row.resolved_at,
      expiresInDays: expiryDaysFromCreatedAt(row.created_at),
      npi: ids.npi || null,
      ccn: ids.ccn || null,
      facilityType: row.parent_facility_type,
      city: row.parent_city,
      state: row.parent_state,
      location: row.parent_location,
      resolutionNote: row.resolution_note,
      currentlyLabel: "Requesting parent facility",
      parentFacilityId: row.parent_facility_id,
      actionFacilityId: selectedId,
      coordinationMode: Boolean(row.coordination_mode),
      currentParentFacilityName: row.current_parent_facility_name ?? null,
      coordinationUnlinkId: row.coordination_unlink_id ?? null,
    });
  });

  receivedUnlink.forEach((row) => {
    if (!selectedId) return;
    const pending = row.status === "pending";
    const resolved = RESOLVED_UNLINK.has(row.status);
    if (!pending && !resolved) return;
    const ids = inboxRegistryIds(row.facility_npi, row.facility_ccn);
    items.push({
      key: `unlink-in-${row.id}`,
      requestId: row.id,
      kind: "unlink",
      direction: "incoming",
      bucket: pending ? "needs" : "resolved",
      variant: pending ? "incoming_unlink" : "resolved_unlink",
      pill: "unlink",
      facilityName: row.facility_name,
      metaLabel: pending ? unlinkIncomingMetaLabel(row.created_at) : "",
      outcomePill: unlinkOutcome(row.status),
      status: row.status,
      createdAt: row.created_at,
      resolvedAt: row.resolved_at,
      expiresInDays: expiryDaysFromCreatedAt(row.created_at),
      npi: ids.npi || null,
      ccn: ids.ccn || null,
      facilityType: row.facility_type,
      reason: row.reason,
      resolutionNote: row.resolution_note,
      currentlyLabel: currentlyLabelForUnlink(row),
      initiatorRole: row.initiator_role,
      actionFacilityId: selectedId,
    });
  });

  input.sent.forEach((row) => {
    if (!parentId) return;
    const meta = linkedMeta(input.linked, row.child_facility_id, row.target_npi, row.id);
    const isInviteNeeded = row.status === "action_needed" && !row.child_facility_id;
    const isInviteSent = row.status === "invite_sent" && !row.child_facility_id;
    const invite = isInviteNeeded || isInviteSent;
    const pendingLink = PENDING_LINK.has(row.status) || (row.status === "action_needed" && Boolean(row.child_facility_id));
    const resolved = RESOLVED_LINK.has(row.status);
    if (!invite && !pendingLink && !resolved) return;

    const name = invite
      ? inviteDisplayName(row.facility_name, row.target_npi)
      : row.facility_name?.trim() || meta?.facility_name?.trim() || `NPI ${row.target_npi}`;
    const days = isInviteSent
      ? expiryDaysFromCreatedAt(row.created_at, INVITE_TTL_DAYS)
      : isInviteNeeded
        ? null
        : expiryDaysFromCreatedAt(row.created_at);
    const ids = inboxRegistryIds(
      row.npi_number || row.target_npi || meta?.npi_number,
      row.ccn_number || meta?.ccn_number,
    );
    const variant: InboxVariant = isInviteNeeded
      ? "invite_needed"
      : resolved
        ? "resolved_link"
        : "outgoing_link";
    items.push({
      key: `link-out-${row.id}`,
      requestId: row.id,
      kind: invite ? "invite" : "link",
      direction: "outgoing",
      bucket: isInviteNeeded ? "needs" : resolved ? "resolved" : "awaiting",
      variant,
      pill: invite ? "invite" : "link",
      facilityName: name,
      metaLabel: isInviteNeeded
        ? "Not on Unicomply"
        : isInviteSent
          ? daysLeftLabel(days)
          : resolved
            ? ""
            : daysLeftLabel(days),
      outcomePill: linkOutcome(row.status),
      status: row.status,
      createdAt: row.created_at,
      resolvedAt: row.resolved_at,
      expiresInDays: days,
      npi: ids.npi || null,
      ccn: ids.ccn || null,
      facilityType: row.facility_type || meta?.facility_type,
      city: row.city || meta?.city,
      state: row.state || meta?.state,
      location: row.location || null,
      resolutionNote: row.resolution_note,
      inviteEmail: row.invite_email,
      childFacilityId: row.child_facility_id,
      actionFacilityId: parentId,
      coordinationMode: Boolean(row.coordination_mode),
    });
  });

  initiatedUnlink.forEach((row) => {
    if (!selectedId) return;
    const pending = row.status === "pending";
    const resolved = RESOLVED_UNLINK.has(row.status);
    if (!pending && !resolved) return;
    const days = expiryDaysFromCreatedAt(row.created_at);
    const ids = inboxRegistryIds(row.facility_npi, row.facility_ccn);
    items.push({
      key: `unlink-out-${row.id}`,
      requestId: row.id,
      kind: "unlink",
      direction: "outgoing",
      bucket: pending ? "awaiting" : "resolved",
      variant: pending ? "outgoing_unlink" : "resolved_unlink",
      pill: "unlink",
      facilityName: row.facility_name,
      metaLabel: pending ? daysLeftLabel(days) : "",
      outcomePill: unlinkOutcome(row.status),
      status: row.status,
      createdAt: row.created_at,
      resolvedAt: row.resolved_at,
      expiresInDays: days,
      npi: ids.npi || null,
      ccn: ids.ccn || null,
      facilityType: row.facility_type,
      reason: row.reason,
      resolutionNote: row.resolution_note,
      currentlyLabel: currentlyLabelForUnlink(row),
      initiatorRole: row.initiator_role,
      actionFacilityId: selectedId,
    });
  });

  return items;
}

export const YOUR_FACILITY_LABEL = "Your facility";

export type ResolvedLinkParties = {
  banner: string;
  sentBy: string;
  sentTo: string;
  resolvedByLabel: string;
  resolvedBy: string;
  /** CCN/NPI rows describe the other facility: receiver when outgoing, sender when incoming. */
  idPrefix: string;
};

/** Who sent and who resolved a link request, from the viewer's side. */
export function resolvedLinkParties(item: NetworkInboxItem): ResolvedLinkParties {
  const other = item.facilityName;
  const outgoing = item.direction === "outgoing";
  const sentBy = outgoing ? YOUR_FACILITY_LABEL : other;
  const sentTo = outgoing ? other : YOUR_FACILITY_LABEL;
  const idPrefix = outgoing ? "Receiver " : "Sender ";
  switch (item.outcomePill) {
    case "cancelled":
      return {
        idPrefix,
        banner: outgoing
          ? `You cancelled your link request to ${other}`
          : `${other} cancelled their link request to you`,
        sentBy,
        sentTo,
        resolvedByLabel: "Cancelled by",
        resolvedBy: sentBy,
      };
    case "accepted":
      return {
        idPrefix,
        banner: outgoing
          ? `${other} accepted your link request`
          : `You accepted the link request from ${other}`,
        sentBy,
        sentTo,
        resolvedByLabel: "Accepted by",
        resolvedBy: sentTo,
      };
    default:
      return {
        idPrefix,
        banner: outgoing
          ? `${other} declined your link request`
          : `You declined the link request from ${other}`,
        sentBy,
        sentTo,
        resolvedByLabel: "Declined by",
        resolvedBy: sentTo,
      };
  }
}

/** Request history: latest action first (AB#11672, AB#11729). */
export function compareResolvedNewestFirst(a: NetworkInboxItem, b: NetworkInboxItem): number {
  const aTime = Date.parse(a.resolvedAt || a.createdAt || "") || 0;
  const bTime = Date.parse(b.resolvedAt || b.createdAt || "") || 0;
  if (bTime !== aTime) return bTime - aTime;
  return b.requestId - a.requestId;
}

export function countOpenNetworkInboxItems(
  input: Parameters<typeof buildNetworkInboxItems>[0],
): number {
  return buildNetworkInboxItems(input).filter((item) => item.bucket !== "resolved").length;
}

function inboxNpiDigits(item: NetworkInboxItem): string {
  return (item.npi || "").replace(/\D/g, "");
}

/** Invite / not-in-Unicomply rows missing registry name, type, or location. */
export function inboxItemNeedsRegistryEnrichment(item: NetworkInboxItem): boolean {
  if (item.variant !== "invite_needed" && item.status !== "invite_sent") return false;
  const npiDigits = inboxNpiDigits(item);
  if (npiDigits.length < 10) return false;
  const location = formatLocationLine(item.city, item.state, item.location);
  return (
    isRegistryPlaceholderName(item.facilityName, npiDigits) ||
    !item.facilityType?.trim() ||
    location === "—" ||
    !item.ccn?.trim()
  );
}

/** Merge NPPES/search hits onto invite inbox rows (same idea as linked-facility enrich). */
export function applyNpiHitsToInboxItems(
  items: NetworkInboxItem[],
  hitsByNpi: Record<string, LinkedFacilityNpiHit>,
): NetworkInboxItem[] {
  return items.map((item) => {
    if (!inboxItemNeedsRegistryEnrichment(item)) return item;
    const npiKey = inboxNpiDigits(item).slice(-10);
    const hit = npiKey.length === 10 ? hitsByNpi[npiKey] : undefined;
    if (!hit) return item;
    const hitName = (hit.name || "").trim();
    const nextName =
      hitName && isRegistryPlaceholderName(item.facilityName, npiKey)
        ? hitName
        : item.facilityName;
    const nextType = item.facilityType?.trim() || hit.facilityType?.trim() || item.facilityType;
    const nextCity = item.city?.trim() || hit.city?.trim() || item.city;
    const nextState = item.state?.trim() || hit.state?.trim() || item.state;
    const nextLocation =
      item.location?.trim() ||
      [nextCity, nextState].filter((part) => Boolean(part && String(part).trim())).join(", ") ||
      item.location;
    const nextCcn = item.ccn?.trim() || (hit.ccn || "").trim() || item.ccn;
    return {
      ...item,
      facilityName: nextName,
      facilityType: nextType,
      city: nextCity,
      state: nextState,
      location: nextLocation,
      ccn: nextCcn || null,
    };
  });
}

export type NetworkRequestFocusMatch = {
  variant: string;
  facilityName?: string | null;
  npi?: string | null;
  body?: string | null;
};

function newestInboxItem(rows: NetworkInboxItem[]): NetworkInboxItem | null {
  if (!rows.length) return null;
  return [...rows].sort((a, b) => {
    const time = (item: NetworkInboxItem) => {
      const parsed = Date.parse(item.resolvedAt || item.createdAt || "");
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const delta = time(b) - time(a);
    if (delta !== 0) return delta;
    return b.requestId - a.requestId;
  })[0];
}

function sameFacilityName(left: string, right: string): boolean {
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/** Body is `{initiator} requested to unlink from your facility.` */
export function initiatorNameFromUnlinkSentBody(body: string): string | null {
  const marker = " requested to unlink from your facility";
  const idx = body.toLowerCase().indexOf(marker);
  if (idx <= 0) return null;
  const name = body.slice(0, idx).trim();
  return name || null;
}

function byNameOrNpi(
  rows: NetworkInboxItem[],
  facilityName?: string | null,
  npi?: string | null,
): NetworkInboxItem[] {
  const name = facilityName?.trim();
  if (name) {
    const named = rows.filter((row) => sameFacilityName(row.facilityName, name));
    if (named.length) return named;
  }
  const digits = (npi || "").replace(/\D/g, "");
  if (digits) {
    const byNpi = rows.filter((row) => (row.npi || "").replace(/\D/g, "") === digits);
    if (byNpi.length) return byNpi;
  }
  return name ? [] : rows;
}

/** Pick the inbox row a notification click should open. */
export function matchInboxItemForNotification(
  items: NetworkInboxItem[],
  focus: NetworkRequestFocusMatch,
): NetworkInboxItem | null {
  if (focus.variant === "unlink_request_sent") {
    const initiator = initiatorNameFromUnlinkSentBody(focus.body || "");
    const pending = items.filter(
      (item) =>
        item.kind === "unlink" && item.direction === "incoming" && item.bucket === "needs",
    );
    if (initiator) {
      return newestInboxItem(
        pending.filter((item) => sameFacilityName(item.facilityName, initiator)),
      );
    }
    return newestInboxItem(pending);
  }
  if (
    focus.variant === "unlink_request_rejected" ||
    focus.variant === "unlink_request_approved"
  ) {
    const status = focus.variant === "unlink_request_rejected" ? "rejected" : "approved";
    const pool = items.filter(
      (item) =>
        item.kind === "unlink" && item.direction === "outgoing" && item.status === status,
    );
    return newestInboxItem(byNameOrNpi(pool, focus.facilityName, focus.npi));
  }
  return null;
}
